import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { ReportProblemOutlined } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles((theme) => ({
  // A warning surface, not coloured text: `warning.main` is the theme's pale
  // yellow `#fef8a0`, which as a text colour on white sits at 1.1:1 against the
  // 4.5:1 of WCAG AA. It goes back to being the background, with its own
  // `contrastText` on top, so the signal stays and the text reads.
  banner: {
    alignItems: 'center',
    backgroundColor: theme.palette.warning.main,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.warning.contrastText,
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    textAlign: 'left',
  },
  testo: {
    flexGrow: 1,
    fontWeight: 600,
  },
}));

/**
 * The one warning surface of the app: something the user should know about
 * before acting, where nothing is refused and no road is closed. A refusal is
 * red and blocks a button; this says what will happen and leaves the gesture
 * available.
 *
 * The icon is not optional: colour alone carries no meaning for whoever cannot
 * separate it from the background.
 *
 * No live region here, deliberately. Every call site mounts the block with its
 * sentence already inside it, and a region that arrives together with its content
 * is the case screen readers treat differently from one another — so `role` on
 * this node would buy an announcement that may never come. What carries the
 * sentence is whatever already frames it where it sits: the `aria-describedby` of
 * the field it belongs to, or the content a dialog reads out on open. A screen
 * that raises one of these after a read, with the focus elsewhere, and wants it
 * spoken, needs a live region already mounted and empty at that spot — which is
 * the screen's own markup, not this component's.
 */
const WarningBanner = ({ children, action, className, testId }) => {
  const classes = useStyles();

  return (
    <div
      className={className ? `${classes.banner} ${className}` : classes.banner}
      data-testid={testId}
    >
      {/* Decorative: the sentence beside it already says what the icon says, and
          announcing «warning» twice is one reading of the block too many. */}
      <ReportProblemOutlined aria-hidden />
      <Typography variant="body2" component="p" className={classes.testo}>
        {children}
      </Typography>
      {action}
    </div>
  );
};

WarningBanner.propTypes = {
  children: PropTypes.node.isRequired,
  // The way out the banner offers, where the screen has one to offer: rendered
  // beside the text, not under it.
  action: PropTypes.node,
  // For what surrounds the block — the margin it takes in its own page — and
  // never for its colours, which are the point of having one component.
  className: PropTypes.string,
  testId: PropTypes.string,
};

export default WarningBanner;
